"use client";

import * as React from "react";

import dynamic from "next/dynamic";

import { Avatar, AvatarFallback, AvatarImage } from "@unreserved/ui/components/avatar";
import { Popover, PopoverTrigger } from "@unreserved/ui/components/popover";
import { cn, propertyDetailsUtils } from "@unreserved/utils";

import { useAuthStore } from "@/stores/auth-store";

const ProfileDialogContent = dynamic(
  () =>
    import("@/app/_components/my-profile/profile-dialog").then((mod) => ({
      default: mod.ProfileDialogContent,
    })),
  { ssr: false },
);

const EditProfileDialog = dynamic(
  () =>
    import("@/app/_components/my-profile/edit-profile/edit-profile-dialog").then((mod) => ({
      default: mod.EditProfileDialog,
    })),
  { ssr: false },
);

const ChangePasswordDialog = dynamic(
  () =>
    import("@/app/_components/my-profile/change-password/change-password-dialog").then((mod) => ({
      default: mod.ChangePasswordDialog,
    })),
  { ssr: false },
);

interface ProfileMenuDesktopProps {
  goBackToDashboard?: boolean;
  onOpenStateChange?: (isOpen: boolean) => void;
}

export function ProfileMenuDesktop({
  goBackToDashboard = false,
  onOpenStateChange,
}: ProfileMenuDesktopProps) {
  const [profileOpen, setProfileOpen] = React.useState(false);
  const [editOpen, setEditOpen] = React.useState(false);
  const [changePassOpen, setChangePassOpen] = React.useState(false);
  const profile = useAuthStore((state) => state.user);

  // Get user data for avatar
  const displayName = propertyDetailsUtils.toString(profile?.fullName) || "User";
  const avatarUrl = profile?.avatar ? propertyDetailsUtils.toString(profile.avatar.fileUrl) : "";

  React.useEffect(() => {
    if (!onOpenStateChange) return;
    const isAnyOpen = profileOpen || editOpen || changePassOpen;
    onOpenStateChange(isAnyOpen);
  }, [profileOpen, editOpen, changePassOpen, onOpenStateChange]);

  return (
    <>
      <Popover open={profileOpen} onOpenChange={setProfileOpen} modal={false}>
        <PopoverTrigger asChild>
          <button
            className={cn(
              "flex size-11 cursor-pointer items-center justify-center rounded-full hover:opacity-90 md:size-12",
            )}
            aria-label="User Profile">
            <Avatar className="size-11 rounded-full md:size-12">
              <AvatarImage src={avatarUrl} alt={displayName} />
              <AvatarFallback className="bg-primary-100 text-typography-900 text-sm font-medium md:text-base">
                {displayName.charAt(0)?.toUpperCase()}
              </AvatarFallback>
            </Avatar>
          </button>
        </PopoverTrigger>
        <ProfileDialogContent
          goBackToDashboard={goBackToDashboard}
          onEdit={() => {
            setProfileOpen(false);
            setEditOpen(true);
          }}
          onChangePassword={() => {
            setProfileOpen(false);
            setChangePassOpen(true);
          }}
          onClose={() => setProfileOpen(false)}
        />
      </Popover>

      {editOpen && (
        <EditProfileDialog
          open={editOpen}
          onOpenChange={setEditOpen}
          onChangePassword={() => {
            setEditOpen(false);
            setChangePassOpen(true);
          }}
        />
      )}

      {changePassOpen && (
        <ChangePasswordDialog open={changePassOpen} onOpenChange={setChangePassOpen} />
      )}
    </>
  );
}
